import { Icon, TooltipWrapper } from "@zapier/design-system";
import { useAtomValue } from "jotai";
import { selectedNodeTypeAtom } from "@zapier/canvas-state";
import classNames from "classnames";

import styles from "./DiagrammingToolbar.module.css";
import { useSelectNodeType } from "./useSelectNodeType";

type Props = {
  isDisabled: boolean;
};

const logoNames = {
  canvasLogoName: "navCanvas",
  tablesLogoName: "navTables",
  interfacesLogoName: "navInterfaces",
  chatbotsLogoName: "navAIChatbot",
} as const;

export function AppNodeOptions({ isDisabled }: Props) {
  const selectedNodeType = useAtomValue(selectedNodeTypeAtom);
  const { maybeSelectNodeType } = useSelectNodeType();

  const { tablesLogoName, interfacesLogoName, chatbotsLogoName } = logoNames;
  const listItemClass = isDisabled
    ? `${styles.dndnode} ${styles.dndnodeDisabled}`
    : styles.dndnode;

  return (
    <ul className={styles.horizontalList}>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "zap",
        })}
      >
        <TooltipWrapper
          position="north"
          content={isDisabled ? "" : "Zap"}
          tooltipId="zap"
        >
          {({ childProps }) => (
            <button
              aria-label="Add node with Zap"
              aria-describedby={childProps["aria-describedby"]}
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("zap");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("zap");
              }}
            >
              <Icon size={28} color="BrandOrange" name="miscBoltAlt" />
            </button>
          )}
        </TooltipWrapper>
      </li>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "table",
        })}
      >
        <TooltipWrapper
          position="north"
          content={isDisabled ? "" : "Table"}
          tooltipId="table"
        >
          {({ childProps }) => (
            <button
              aria-describedby={childProps["aria-describedby"]}
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("table");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("table");
              }}
            >
              <Icon size={28} color="BrandOrange" name={tablesLogoName} />
            </button>
          )}
        </TooltipWrapper>
      </li>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "interface",
        })}
      >
        <TooltipWrapper
          position="north"
          content={isDisabled ? "" : "Interface"}
          tooltipId="interface"
        >
          {({ childProps }) => (
            <button
              aria-describedby={childProps["aria-describedby"]}
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("interface");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("interface");
              }}
            >
              <Icon size={28} color="BrandOrange" name={interfacesLogoName} />
            </button>
          )}
        </TooltipWrapper>
      </li>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "chatbots",
        })}
      >
        <TooltipWrapper
          position="north"
          content={isDisabled ? "" : "Chatbots"}
          tooltipId="chatbots"
        >
          {({ childProps }) => (
            <button
              aria-describedby={childProps["aria-describedby"]}
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("chatbots");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("chatbots");
              }}
              draggable={!isDisabled}
            >
              <Icon size={28} color="BrandOrange" name={chatbotsLogoName} />
            </button>
          )}
        </TooltipWrapper>
      </li>
    </ul>
  );
}
